/**
 * Performance configuration and utilities
 * Centralized settings to optimize Core Web Vitals, especially INP
 */

// Three.js performance settings
export const THREEJS_CONFIG = {
  // Reduced particle count for better performance
  STAR_COUNT: 800, // Reduced from 1500

  // Earth geometry optimization
  EARTH_SEGMENTS: 32, // Reduced from 64

  // Canvas settings for better performance
  CANVAS_SETTINGS: {
    antialias: false, // Disabled for better performance
    powerPreference: "high-performance" as const,
    stencil: false,
    depth: false, // For background only
  },

  // Frame rate settings
  PERFORMANCE_SETTINGS: {
    min: 0.5, // Allow frame rate to drop for better INP
  },
} as const;

// Interaction debounce/throttle delays
export const INTERACTION_DELAYS = {
  RESIZE_THROTTLE: 100,
  SCROLL_THROTTLE: 16, // ~60fps
  CLICK_DEBOUNCE: 50,
} as const;

// Loading optimization
export const LOADING_CONFIG = {
  LOADING_SCREEN_DELAY: 1000,
  TEXTURE_LOADING_TIMEOUT: 5000,
} as const;

/**
 * Utility to check if device supports high performance features
 */
export const getDeviceCapabilities = () => {
  const canvas = document.createElement("canvas");
  const gl =
    canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

  return {
    supportsWebGL: !!gl,
    isMobile:
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
    hasHighPerformanceGPU: navigator.hardwareConcurrency > 4,
    supportsRequestIdleCallback: "requestIdleCallback" in window,
  };
};

/**
 * Adaptive performance settings based on device capabilities
 */
export const getAdaptiveSettings = () => {
  const capabilities = getDeviceCapabilities();

  return {
    starCount: capabilities.isMobile ? 400 : THREEJS_CONFIG.STAR_COUNT,
    earthSegments: capabilities.isMobile ? 32 : THREEJS_CONFIG.EARTH_SEGMENTS,
    enableAntialiasing:
      capabilities.hasHighPerformanceGPU && !capabilities.isMobile,
    frameloop: "always",
  };
};
